import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Briefcase, FileText, ExternalLink, FilePlus2, ArrowRight } from "lucide-react";

const STATUS_COLOR = {
  "Påbörjad": "bg-muted text-muted-foreground",
  "Skickad": "bg-[hsl(var(--brand-azure))]/10 text-[hsl(var(--brand-azure))]",
  "Intervju": "bg-primary/10 text-primary",
  "Erbjudande": "bg-[hsl(var(--success))]/10 text-[hsl(150,45%,30%)]",
  "Avslag": "bg-destructive/10 text-destructive",
};

export default function Ansokningar() {
  const [apps, setApps] = useState(null);
  const [statuses, setStatuses] = useState([]);

  const load = () => api.get("/applications").then((r) => { setApps(r.data.applications); setStatuses(r.data.statuses); });
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try { await api.put(`/applications/${id}/status`, { status }); toast.success("Status uppdaterad"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  if (!apps) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div>
      <PageHeader
        title="Mina ansökningar"
        description="En samlad vy över de jobb du sökt, deras status och det CV du kopplat till varje ansökan."
      />

      {apps.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-2 border-border p-10 text-center" data-testid="applications-empty">
          <Briefcase className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-4">Inga ansökningar ännu. Skapa ett skräddarsytt CV mot ett jobb så dyker det upp här.</p>
          <Link to="/app/skapa-cv"><Button className="rounded-full"><FilePlus2 className="h-4 w-4 mr-2" /> Skapa CV mot jobb</Button></Link>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="applications-list">
          {apps.map((a) => (
            <Card key={a.id} className="rounded-2xl border-border p-5" data-testid={`application-${a.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-head font-semibold text-lg">{a.title || "Namnlös tjänst"}</h3>
                    {a.company && <span className="text-sm text-muted-foreground">· {a.company}</span>}
                  </div>
                  {a.url && (
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline">
                      <ExternalLink className="h-3 w-3" /> Annonslänk
                    </a>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {a.cvs.length > 0 ? a.cvs.map((c) => (
                      <Link key={c.id} to={`/app/cv/${c.id}`}>
                        <Badge variant="secondary" className="rounded-full cursor-pointer hover:bg-primary/10" data-testid={`app-cv-${c.id}`}>
                          <FileText className="h-3 w-3 mr-1" /> {c.title || "CV"}{c.status === "final" ? " ✓" : ""}
                        </Badge>
                      </Link>
                    )) : <span className="text-xs text-muted-foreground">Inget CV kopplat ännu</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs rounded-full px-2.5 py-1 font-medium ${STATUS_COLOR[a.status] || "bg-muted"}`}>{a.status}</span>
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="w-[150px] rounded-full" data-testid={`status-select-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
